import { useEffect, useMemo, useRef, useState } from 'react';

import { useCommendaAiActions } from '../CommendaAiActionsContext';
import {
  DocumentReadingRunHelper,
  type IDocumentReadingRunSnapshot,
} from './DocumentReadingRunHelper';

/** Shows the reader's finish messages in the open chat. The backend posts one when a reading run
 * completes, when a re-read ends and when a write-back ends, a few seconds after the state moves. On such
 * a move seen here, it waits for an idle chat, re-reads the conversation and appends the assistant
 * messages not on screen, and re-reads again after each FINISH_MESSAGE_RETRY_DELAYS_MS wait until one
 * shows. */
export function useDocumentReaderChatRefresh(
  {
    runId,
    runState,
    writeBackProposalId,
    writeBackState,
    reExtractState,
  }: Readonly<IDocumentReadingRunSnapshot>,
  isNewInSession: boolean
): {
  isLoading: boolean;
  error: unknown;
} {
  const actions = useCommendaAiActions();
  // The re-read waiting for an idle chat, by its place in the retry waits; null while none waits.
  const [pendingAttempt, setPendingAttempt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const previousRef = useRef<IDocumentReadingRunSnapshot | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isMountedRef = useRef(true);

  const snapshot = useMemo<IDocumentReadingRunSnapshot>(
    () => ({
      runId,
      runState,
      writeBackProposalId,
      writeBackState,
      reExtractState,
    }),
    [runId, runState, writeBackProposalId, writeBackState, reExtractState]
  );
  const isChatIdle = useMemo(
    () => (actions ? !actions.isGenerating : false),
    [actions]
  );
  const showNewAssistantMessages = actions?.showNewAssistantMessages;

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = snapshot;
    if (
      !DocumentReadingRunHelper.hasJustFinished(
        previous,
        snapshot,
        isNewInSession
      )
    )
      return;
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    setPendingAttempt(0);
  }, [snapshot, isNewInSession]);

  // A pending retry ends with the dock that holds it: on a conversation switch and on unmount.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (pendingAttempt === null || !isChatIdle || !showNewAssistantMessages)
      return;
    const attempt = pendingAttempt;
    const delays = DocumentReadingRunHelper.FINISH_MESSAGE_RETRY_DELAYS_MS;
    setPendingAttempt(null);
    setIsLoading(true);
    setError(null);
    showNewAssistantMessages()
      .then((shownCount) => {
        if (shownCount > 0 || attempt >= delays.length || !isMountedRef.current)
          return;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(
          () => setPendingAttempt(attempt + 1),
          delays[attempt]
        );
      })
      .catch((refreshError: unknown) => setError(refreshError))
      .finally(() => setIsLoading(false));
  }, [pendingAttempt, isChatIdle, showNewAssistantMessages]);

  return { isLoading, error };
}
